import { api, handleApiError } from '../client'
import { buildODataQuery, type ODataQueryParams } from '../odata'
import type {
  TemplateHub,
  TemplateHubDetail,
  TemplateHubTree,
  TemplateHubBreadcrumb,
  CreateHubRequest,
  UpdateHubRequest,
  HubsODataResponse,
  TemplateHubMembership,
} from '../types/hubs'

export type {
  TemplateHub,
  TemplateHubDetail,
  TemplateHubTree,
  TemplateHubBreadcrumb,
  CreateHubRequest,
  UpdateHubRequest,
  HubsODataResponse,
  TemplateHubMembership,
}

export const hubService = {
  /**
   * Gets hubs with OData query support.
   * Supports filtering, sorting, paging via OData parameters.
   */
  async getHubs(params?: ODataQueryParams): Promise<HubsODataResponse> {
    try {
      const queryString = params ? buildODataQuery(params) : ''
      const response = await api.get<HubsODataResponse | TemplateHub[]>(`/v1/hubs${queryString}`)

      // Handle both OData response and array response for backwards compatibility
      if (Array.isArray(response.data)) {
        return {
          value: response.data,
          '@odata.count': response.data.length
        }
      }
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets a single hub by ID with full details including parent, children, and breadcrumbs.
   */
  async getHub(id: string): Promise<TemplateHubDetail> {
    try {
      const response = await api.get<TemplateHubDetail>(`/v1/hubs/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets the full hub tree structure (hierarchical).
   */
  async getHubTree(): Promise<TemplateHubTree[]> {
    try {
      const response = await api.get<TemplateHubTree[]>('/v1/hubs/tree')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets breadcrumb trail for a hub.
   */
  async getHubBreadcrumbs(id: string): Promise<TemplateHubBreadcrumb[]> {
    try {
      const response = await api.get<TemplateHubBreadcrumb[]>(`/v1/hubs/${id}/breadcrumbs`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Creates a new hub.
   */
  async createHub(data: CreateHubRequest): Promise<TemplateHub> {
    try {
      const response = await api.post<TemplateHub>('/v1/hubs', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates an existing hub.
   */
  async updateHub(id: string, data: UpdateHubRequest): Promise<TemplateHub> {
    try {
      const response = await api.put<TemplateHub>(`/v1/hubs/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deletes a hub.
   */
  async deleteHub(id: string): Promise<void> {
    try {
      await api.delete(`/v1/hubs/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Adds templates to a hub.
   */
  async addTemplatesToHub(hubId: string, templateIds: string[]): Promise<void> {
    try {
      await api.post(`/v1/hubs/${hubId}/templates`, { templateIds })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Removes templates from a hub.
   */
  async removeTemplatesFromHub(hubId: string, templateIds: string[]): Promise<void> {
    try {
      await api.delete(`/v1/hubs/${hubId}/templates`, {
        data: { templateIds }
      })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets templates in a hub.
   */
  async getHubTemplates(hubId: string): Promise<TemplateHubMembership[]> {
    try {
      const response = await api.get<TemplateHubMembership[]>(`/v1/hubs/${hubId}/templates`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Moves a hub to a new parent.
   */
  async moveHub(id: string, newParentId: string | null): Promise<TemplateHub> {
    try {
      const response = await api.post<TemplateHub>(`/v1/hubs/${id}/move`, {
        parentId: newParentId
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Duplicates a hub (optionally with its children).
   */
  async duplicateHub(id: string, name: string, includeChildren: boolean = false): Promise<TemplateHub> {
    try {
      const response = await api.post<TemplateHub>(`/v1/hubs/${id}/duplicate`, {
        name,
        includeChildren
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
